import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Typography, Tabs } from '@ann/web-components';
import { useDashboardUI, useDashboardScroll } from '../../context';
import { useScrollToTop } from '../../../../../contexts/ScrollToTopContext';
import { DASHBOARD_TABS } from '../../constants';
import { DASHBOARD_LIST_COPY } from '../../constants/dashboardBuilderCopy';
import {
  HeaderWrapper,
  TitleRow,
  TitleBlock,
  ButtonWrapper,
  TabsRow,
  Sentinel,
  CompactTopbar,
  CompactTopbarInner,
} from './DashboardListHeader.styled';

const DashboardListHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveTab } = useDashboardUI();
  const dashboardScrollRef = useDashboardScroll();
  const { containerRef: appContainerRef } = useScrollToTop();
  const scrollContainerRef = dashboardScrollRef ?? appContainerRef;
  const sentinelRef = useRef(null);
  const [showCompact, setShowCompact] = useState(false);
  const [containerRect, setContainerRect] = useState({ width: null, left: null });

  const currentTab = useMemo(() => {
    return location.pathname.includes('/templates')
      ? DASHBOARD_TABS.TEMPLATES
      : DASHBOARD_TABS.CREATED;
  }, [location.pathname]);

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      navigate(
        tabId === DASHBOARD_TABS.CREATED ? '/dashboards' : '/dashboards/templates'
      );
    },
    [setActiveTab, navigate]
  );

  const handleNewDashboard = useCallback(() => {
    navigate('new');
  }, [navigate]);

  const { title, description, createButton, tabs } = DASHBOARD_LIST_COPY.header;

  const compactTitle = currentTab === DASHBOARD_TABS.CREATED ? title : tabs.templates;

  useEffect(() => {
    if (!sentinelRef.current || !scrollContainerRef?.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowCompact(!entry.isIntersecting);
      },
      {
        root: scrollContainerRef.current,
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    let rafId = null;
    const prevRef = { width: null, left: null };

    const updateRect = () => {
      const rect = scrollContainer.getBoundingClientRect();
      const width = Math.round(rect.width);
      const left = Math.round(rect.left);
      if (prevRef.width === width && prevRef.left === left) return;
      prevRef.width = width;
      prevRef.left = left;
      setContainerRect({ width, left });
    };

    const throttledUpdate = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateRect();
      });
    };

    updateRect();
    const resizeObserver = new ResizeObserver(throttledUpdate);
    resizeObserver.observe(scrollContainer);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return (
    <>
      <CompactTopbar
        $show={showCompact}
        $containerWidth={containerRect.width}
        $containerLeft={containerRect.left}
      >
        <CompactTopbarInner>
          <Typography category="title" size="medium" weight="bold">
            {compactTitle}
          </Typography>
          <Button
            label={createButton}
            iconRight="plus"
            type="gradient"
            size="small"
            onClick={handleNewDashboard}
          />
        </CompactTopbarInner>
      </CompactTopbar>

      <HeaderWrapper>
        <Sentinel ref={sentinelRef} aria-hidden="true" />
        <TitleRow>
          <TitleBlock>
            <Typography category="title" size="large" weight="bold">
              {title}
            </Typography>
            <Typography category="body" size="medium" weight="regular">
              {description}
            </Typography>
          </TitleBlock>
          <ButtonWrapper>
            <Button
              label={createButton}
              iconRight="plus"
              type="gradient"
              size="medium"
              onClick={handleNewDashboard}
            />
          </ButtonWrapper>
        </TitleRow>

        <TabsRow>
          <Tabs
            key={currentTab}
            defaultIndex={currentTab}
            size="medium"
            onChange={handleTabChange}
          >
            <Tabs.List>
              <Tabs.Tab index={DASHBOARD_TABS.CREATED} size="medium">
                {tabs.created}
              </Tabs.Tab>
              <Tabs.Tab index={DASHBOARD_TABS.TEMPLATES} size="medium">
                {tabs.templates}
              </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </TabsRow>
      </HeaderWrapper>
    </>
  );
};

export default DashboardListHeader;
